import { useEffect, useState } from 'react';
import { Drawer } from '@mui/material';
import icons from '../constants/icons';
import { ReactSVG } from 'react-svg';
import images from '../constants/images';
import Text from './Text';
import { fontSizes } from '../constants/constants';
import dynamicfunction from '../utils/dynamicfunction';
import Language from './Language';
import Button from './Button';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import ViewReverse from './ViewReverse';
import { sidebarMenu } from '../config/config';
import { cn } from '../utils/utils';
import { clientSuspendedToast } from '../utils/functions';

const MobileMenu = ({ isDrawer, onClose, logoutText, onLogout, clientDetails, getContentKey, user, isCorporateForm, isIndividualForm }) => {
  const navigate = useNavigate();
  const pathname = useLocation();
  const { unreadCount } = useSelector((state) => state.notification);

  const { TextWeight, isArabic } = dynamicfunction();

  const sections = sidebarMenu(pathname?.pathname);

  const [expandedKey, setExpandedKey] = useState(
    () => sections?.find((section) => section?.isActive)?.key ?? null
  );

  useEffect(() => {
    const activeKey = sections?.find((section) => section?.isActive)?.key;
    if (activeKey) setExpandedKey(activeKey);
  }, [pathname?.pathname]);

  const onPressMenu = (route) => {
    navigate(route);
    onClose();
  };

  const onSectionClick = (section, is_disabled) => {
    if (is_disabled) return;
    if (section?.childs?.length > 0) {
      setExpandedKey((prev) => (prev === section?.key ? null : section?.key));
      return;
    }
    const is_suspended = section?.key === 'cash_management' && user?.isClientSuspended;
    onPressMenu(is_suspended ? section?.suspended_route : section?.route);
  };

  const onChildClick = (child, is_disabled) => {
    if (is_disabled) return;
    const is_suspend = child?.suspendedRoute && user?.isClientSuspended;
    if (is_suspend) {
      clientSuspendedToast(isArabic);
      onClose();
    } else {
      onPressMenu(child?.route);
    }
  };

  return (
    <Drawer open={isDrawer} onClose={onClose} anchor={isArabic ? "left" : 'right'}>
      <div className={`flex flex-col justify-between max-w-[300px] min-w-[300px] h-full bg-white p-4`}>
        <div>
          <ReactSVG src={icons.cross} className='cursor-pointer' onClick={onClose} />
          <ViewReverse className={'justify-between lg:justify-center items-center mt-3'}>
            <img src={images.logo} className='h-10 block lg:hidden' />
            <div className='block lg:hidden'>
              <Language isLogin />
            </div>
          </ViewReverse>

          <div className='h-10 block lg:hidden' />
          <ViewReverse className='items-center justify-between block lg:hidden'>
            <div>
              <Text
                text={clientDetails?.clientInformation?.clientName ?? ''}
                color={'text-dark-grey'}
                className={'uppercase flex-1'}
                weight={TextWeight.regular}
                size={fontSizes.h17}
                isTruncate={false}
              />
              <Text text={clientDetails?.clientInformation?.clientEmail ?? ''} size={fontSizes.h11} />
            </div>
            {user && !isCorporateForm && !isIndividualForm && (
              <ReactSVG
                src={unreadCount >= 1 ? icons?.notificationIcon : icons?.notificationIconNoBadge}
                className='cursor-pointer [&_path]:stroke-primary'
                onClick={() => onPressMenu('/notifications')}
              />
            )}
          </ViewReverse>
          {!user && !isCorporateForm && !isIndividualForm && (
            <ViewReverse className={'gap-3 items-center cursor-pointer block sm:hidden'} onClick={() => navigate('/help/faqs')} isReverse={false}>
              <ReactSVG src={icons?.Faqs_black_icon} />
              <Text text={getContentKey('faqs')} color={'text-black'} size={fontSizes.h15} />
            </ViewReverse>
          )}

          <div className='flex flex-col gap-4 mt-4'>
            {user && !isCorporateForm && !isIndividualForm && (
              sections?.map((section, index) => {
                const disabled = user ? false : index !== 0;

                return (
                  <div
                    key={section?.key ?? index}
                    className={cn('rounded-md transition-all duration-300 ease-in-out', section?.isActive ? 'bg-white' : 'bg-transparent', disabled ? 'opacity-50 cursor-not-allowed' : 'opacity-100 cursor-pointer')}
                  >
                    <ViewReverse
                      className={'items-center justify-between'}
                      onClick={() => onSectionClick(section, disabled)}
                    >
                      <ViewReverse className={'gap-2 items-center'}>
                        <ReactSVG
                          src={section?.isActive ? section?.activeIcon : section?.disableIcon}
                          className='scale-75 md:scale-100'
                        />
                        <Text
                          text={getContentKey(section?.key)}
                          size={fontSizes.h14}
                          color={section?.isActive ? 'text-primary' : 'text-black'}
                        />
                      </ViewReverse>
                      {section?.childs?.length > 0 && (
                        <ReactSVG
                          src={icons.upArrowIcon}
                          className={cn(expandedKey === section?.key ? '' : '-rotate-180')}
                        />
                      )}
                    </ViewReverse>

                    {expandedKey === section?.key && section?.childs?.length > 0 && (
                      <div className={'flex flex-col gap-3 mt-3 ps-3'}>
                        {section?.childs?.map((child) => {
                          const is_child_disabled = disabled || (child?.withdrawalRoute && !clientDetails?.enableWithdrawal);
                          return (
                            <ViewReverse
                              key={child?.key}
                              className={cn('gap-2 items-center', is_child_disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
                              onClick={() => onChildClick(child, is_child_disabled)}
                            >
                              <ReactSVG
                                src={child?.isActive ? child?.activeIcon : child?.disableIcon}
                                className='scale-75 md:scale-100'
                              />
                              <Text
                                text={getContentKey(child?.key)}
                                size={fontSizes.h14}
                                color={child?.isActive ? 'text-primary' : 'text-black'}
                              />
                            </ViewReverse>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
        <Button
          text={logoutText}
          bgColor='bg-transparent'
          color='text-black'
          className={'border border-grey w-28 block sm:hidden'}
          weight={TextWeight.regular}
          onClick={onLogout}
        />
      </div>
    </Drawer>
  );
};

export default MobileMenu;
